import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardHeader from '@mui/material/CardHeader'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'
import {
  ageMinutes,
  type MetarObservation,
  type PointForecast,
  type StationObservation,
  type TafForecast,
  type WeatherFreshness,
  type WeatherProviderId,
} from '@jupi/shared'
import { formatClockTime, formatTime, RemoteContent, useServerClock } from '@jupi/ui'
import { useAirportWeather } from '../../api/weather'
import { SunTimesRow } from './SunTimesRow'
import { TafDecoded } from './TafDecoded'
import {
  cloudsText,
  compassPoint,
  FLIGHT_CATEGORY_COLORS,
  tafLines,
  temperatureText,
  utcDayAndTime,
  visibilityText,
  windText,
} from './weatherFormat'

const PROVIDER_ATTRIBUTION: Record<WeatherProviderId, string> = {
  aviationweather: 'AviationWeather.gov (NOAA)',
  fmi: 'Ilmatieteen laitos / FMI (CC BY 4.0)',
}

// Ages (min) past which a reading is shown as old, even if fetched fresh.
const OLD_AFTER_MIN = { metar: 60, localStation: 30 } as const

const mono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' } as const

export function AirportWeatherCard({ icao }: { icao: string }) {
  const { t } = useTranslation()
  const { data, isLoading, error } = useAirportWeather(icao)
  const { utcMs: now } = useServerClock()

  const providers = new Set<WeatherProviderId>()
  for (const product of [data?.metar, data?.taf, data?.localStation, data?.pointForecast]) {
    if (product) providers.add(product.provider)
  }

  return (
    <Card>
      <CardHeader
        avatar={<Icon icon="mdi:airport" width={28} />}
        title={
          <Typography variant="h6" component="h2">
            {icao}
            {data ? ` – ${data.airport.name}` : ''}
          </Typography>
        }
        subheader={data ? <SunTimesRow lat={data.airport.lat} lon={data.airport.lon} /> : null}
        sx={{ pb: 0 }}
      />
      <CardContent>
        <RemoteContent isLoading={isLoading} error={error}>
          {data ? (
            <Stack spacing={2} divider={<Divider flexItem />}>
              {data.airport.products.includes('metar') ? <MetarSection metar={data.metar} now={now} /> : null}
              {data.airport.products.includes('taf') ? <TafSection taf={data.taf} now={now} /> : null}
              {data.airport.products.includes('localStation') ? (
                <StationSection observation={data.localStation} now={now} />
              ) : null}
              {data.airport.products.includes('pointForecast') ? (
                <PointForecastSection forecast={data.pointForecast} now={now} />
              ) : null}
              <Typography variant="caption" color="text.secondary">
                {t('weather.sources')}: {[...providers].map((p) => PROVIDER_ATTRIBUTION[p]).join(' · ')}
              </Typography>
            </Stack>
          ) : null}
        </RemoteContent>
      </CardContent>
    </Card>
  )
}

function SectionHeader({
  label,
  kind,
  subtitle,
  children,
}: {
  label: string
  kind: 'official' | 'unofficial'
  subtitle?: string
  children?: ReactNode
}) {
  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
        <Chip
          label={label}
          size="small"
          color={kind === 'official' ? 'primary' : 'default'}
          variant={kind === 'official' ? 'filled' : 'outlined'}
          sx={{ fontWeight: 700 }}
        />
        {children}
      </Stack>
      {subtitle ? (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  )
}

function Freshness({ at, product, now, oldAfterMin }: { at: string; product: WeatherFreshness; now: number; oldAfterMin?: number }) {
  const { t } = useTranslation()
  const age = ageMinutes(at, now)
  const old = oldAfterMin !== undefined && age > oldAfterMin
  const ageText = age < 90 ? t('weather.minutesAgo', { count: age }) : t('weather.hoursAgo', { count: Math.floor(age / 60) })
  return (
    <>
      <Typography variant="body2" color={old ? 'warning.main' : 'text.secondary'} sx={{ whiteSpace: 'nowrap' }}>
        {formatClockTime(at, 'UTC')} UTC · {ageText}
      </Typography>
      {product.stale ? (
        <Chip
          size="small"
          color="warning"
          icon={<Icon icon="mdi:alert-outline" />}
          label={
            product.staleSince
              ? t('weather.staleSince', { time: formatTime(product.staleSince) })
              : t('weather.stale')
          }
        />
      ) : null}
    </>
  )
}

function Figures({ rows }: { rows: [string, ReactNode | null][] }) {
  const { t } = useTranslation()
  return (
    <Box
      component="dl"
      sx={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 2, rowGap: 0.5, m: 0 }}
    >
      {rows.map(([label, value]) => (
        <Box key={label} sx={{ display: 'contents' }}>
          <Typography component="dt" variant="body2" color="text.secondary">
            {label}
          </Typography>
          <Typography component="dd" variant="body1" sx={{ m: 0, fontWeight: value === null ? 400 : 500 }}>
            {value ?? <Box component="span" sx={{ color: 'text.disabled' }}>{t('weather.notReported')}</Box>}
          </Typography>
        </Box>
      ))}
    </Box>
  )
}

function WindArrow({ directionDeg }: { directionDeg: number | null }) {
  if (directionDeg === null) return null
  // Wind direction is where it blows FROM; the arrow shows where it goes.
  return (
    <Box component="span" sx={{ display: 'inline-flex', verticalAlign: 'middle', mr: 0.5 }}>
      <Icon icon="mdi:navigation" width={16} style={{ transform: `rotate(${directionDeg + 180}deg)` }} />
    </Box>
  )
}

function Unavailable() {
  const { t } = useTranslation()
  return (
    <Typography variant="body2" color="text.secondary">
      {t('weather.unavailable')}
    </Typography>
  )
}

function MetarSection({ metar, now }: { metar: MetarObservation | null; now: number }) {
  const { t } = useTranslation()
  const wind = metar?.wind
  return (
    <Stack spacing={1.5}>
      <SectionHeader label="METAR" kind="official" subtitle={t('weather.metarSubtitle')}>
        {metar?.flightCategory ? (
          <Chip
            size="small"
            label={metar.flightCategory}
            sx={{ bgcolor: FLIGHT_CATEGORY_COLORS[metar.flightCategory], color: '#fff', fontWeight: 700 }}
          />
        ) : null}
        {metar ? <Freshness at={metar.observedAt} product={metar} now={now} oldAfterMin={OLD_AFTER_MIN.metar} /> : null}
      </SectionHeader>
      {metar ? (
        <>
          <Figures
            rows={[
              [
                t('weather.wind'),
                wind ? (
                  <>
                    <WindArrow directionDeg={wind.directionDeg} />
                    {windText(wind.directionDeg, wind.speedKt, wind.gustKt, t('weather.calm'))}
                    {wind.variableFromDeg !== null && wind.variableToDeg !== null ? (
                      <Typography component="span" variant="body2" color="text.secondary">
                        {' '}
                        {t('weather.variableBetween', { from: wind.variableFromDeg, to: wind.variableToDeg })}
                      </Typography>
                    ) : null}
                  </>
                ) : null,
              ],
              [t('weather.visibility'), visibilityText(metar.visibilityM)],
              [t('weather.clouds'), cloudsText(metar.clouds, metar.cavok)],
              [t('weather.temperatureDewPoint'), temperatureText(metar.temperatureC, metar.dewPointC)],
              [t('weather.qnh'), metar.qnhHpa === null ? null : `${metar.qnhHpa} hPa`],
              ...(metar.weather.length > 0
                ? ([[t('weather.presentWeather'), metar.weather.join(' ')]] as [string, string][])
                : []),
            ]}
          />
          <RawText>{metar.raw}</RawText>
        </>
      ) : (
        <Unavailable />
      )}
    </Stack>
  )
}

function RawText({ children }: { children: string }) {
  return (
    <Box
      component="pre"
      sx={{
        ...mono,
        m: 0,
        p: 1.5,
        borderRadius: 1,
        bgcolor: 'action.hover',
        fontSize: '0.85rem',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {children}
    </Box>
  )
}

function TafSection({ taf, now }: { taf: TafForecast | null; now: number }) {
  const { t } = useTranslation()
  const [view, setView] = useState<'decoded' | 'raw'>('decoded')
  return (
    <Stack spacing={1.5}>
      <SectionHeader
        label="TAF"
        kind="official"
        subtitle={
          taf
            ? t('weather.tafValid', { from: utcDayAndTime(taf.validFrom), to: utcDayAndTime(taf.validTo) })
            : undefined
        }
      >
        {taf?.amendment ? (
          <Chip size="small" variant="outlined" label={t(taf.amendment === 'AMD' ? 'weather.taf.amended' : 'weather.taf.corrected')} />
        ) : null}
        {taf ? <Freshness at={taf.issuedAt} product={taf} now={now} /> : null}
      </SectionHeader>
      {taf ? (
        <>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={view}
            onChange={(_, next: 'decoded' | 'raw' | null) => next && setView(next)}
          >
            <ToggleButton value="decoded">{t('weather.taf.decoded')}</ToggleButton>
            <ToggleButton value="raw">{t('weather.taf.raw')}</ToggleButton>
          </ToggleButtonGroup>
          {view === 'decoded' ? <TafDecoded taf={taf} now={now} /> : <RawText>{tafLines(taf.raw)}</RawText>}
        </>
      ) : (
        <Unavailable />
      )}
    </Stack>
  )
}

function StationSection({ observation, now }: { observation: StationObservation | null; now: number }) {
  const { t, i18n } = useTranslation()
  const decimal = (n: number) => n.toLocaleString(i18n.language, { maximumFractionDigits: 1, useGrouping: false })
  const o = observation
  return (
    <Stack spacing={1.5}>
      <SectionHeader
        label={t('weather.station')}
        kind="unofficial"
        subtitle={
          o
            ? t('weather.stationSubtitle', {
                name: o.station.name,
                distance: decimal(o.station.distanceKm),
                direction: t(`weather.compass.${compassPoint(o.station.bearingDeg)}`),
              })
            : undefined
        }
      >
        {o ? <Freshness at={o.observedAt} product={o} now={now} oldAfterMin={OLD_AFTER_MIN.localStation} /> : null}
      </SectionHeader>
      {o ? (
        <>
          <Figures
            rows={[
              [
                t('weather.wind'),
                o.windSpeedKt === null ? (
                  <Box component="span" sx={{ color: 'text.disabled', fontWeight: 400 }}>
                    {t('weather.notMeasured')}
                  </Box>
                ) : (
                  <>
                    <WindArrow directionDeg={o.windDirectionDeg} />
                    {windText(o.windDirectionDeg, o.windSpeedKt, o.windGustKt, t('weather.calm'))}
                  </>
                ),
              ],
              [t('weather.visibility'), visibilityText(o.visibilityM)],
              [t('weather.cloudCover'), o.cloudCoverOktas === null ? null : `${o.cloudCoverOktas}/8`],
              [t('weather.temperatureDewPoint'), temperatureText(o.temperatureC, o.dewPointC)],
              [
                t('weather.seaLevelPressure'),
                o.pressureHpa === null ? null : `${decimal(o.pressureHpa)} hPa`,
              ],
              [t('weather.precipitation1h'), o.precipitation1hMm === null ? null : `${decimal(o.precipitation1hMm)} mm`],
            ]}
          />
          <Typography variant="caption" color="text.secondary">
            {t('weather.notAnAviationObservation')}
          </Typography>
        </>
      ) : (
        <Unavailable />
      )}
    </Stack>
  )
}

const FORECAST_HOURS = 12

function PointForecastSection({ forecast, now }: { forecast: PointForecast | null; now: number }) {
  const { t, i18n } = useTranslation()
  const hours = (forecast?.hours ?? []).filter((h) => Date.parse(h.time) > now - 3_600_000).slice(0, FORECAST_HOURS)
  return (
    <Stack spacing={1.5}>
      <SectionHeader label={t('weather.forecast')} kind="unofficial" subtitle={t('weather.forecastSubtitle')}>
        {forecast?.stale ? <Freshness at={forecast.retrievedAt} product={forecast} now={now} /> : null}
      </SectionHeader>
      {hours.length > 0 ? (
        <Box sx={{ overflowX: 'auto', mx: -2, px: 2 }}>
          <Table size="small" sx={{ '& td, & th': { px: 1, whiteSpace: 'nowrap' } }}>
            <TableHead>
              <TableRow>
                <TableCell>{t('weather.time')}</TableCell>
                <TableCell>{t('weather.wind')}</TableCell>
                <TableCell align="right">{t('weather.temperatureDewPointShort')}</TableCell>
                <TableCell align="right">{t('weather.cloudCoverShort')}</TableCell>
                <TableCell align="right">{t('weather.precipitationShort')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {hours.map((h) => (
                <TableRow key={h.time}>
                  <TableCell>{formatTime(h.time)}</TableCell>
                  <TableCell>
                    <WindArrow directionDeg={h.windDirectionDeg} />
                    {windText(h.windDirectionDeg, h.windSpeedKt, h.gustKt, t('weather.calm')) ?? '–'}
                  </TableCell>
                  <TableCell align="right">{temperatureText(h.temperatureC, h.dewPointC) ?? '–'}</TableCell>
                  <TableCell align="right">{h.cloudCoverOktas === null ? '–' : `${h.cloudCoverOktas}/8`}</TableCell>
                  <TableCell align="right">{h.precipitationMm === null ? '–' : h.precipitationMm.toLocaleString(i18n.language)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : (
        <Unavailable />
      )}
    </Stack>
  )
}
